import React, {useEffect, useRef, useState} from 'react'
import {Divider, withStyles} from '@material-ui/core';
import PropTypes from 'prop-types';
import Form from 'components/Form';
import initialValues from './constants/initialValues'
import AdditionalButtons from './components/AdditionalButtons';
import {connect} from 'react-redux'
import Loading from 'components/Loading';
import {withRouter} from "react-router";
import SaveFormikValues from 'components/SaveFormikValues';
import SortableContainer from 'components/complex/sortable/SortableContainer';
import validationSchema from './constants/validationSchema'
import DealingsService from 'services/DealingsService';
import Element from './components/Element';
import setFormikValuesTouched from 'libs/setFormikValuesTouched';
import ExecuteFunction from 'components/ExecuteFunction';
import {isDemandModification} from "libs/getDemandStatus";
import ModificationService from "services/ModificationService";
import DemandValidationMessages from "components/DemandValidationMessages";
import {
    clearStepperStateCloseValidator,
    setStepperStateCloseValidator,
    setStepperStateDeleteFormDraftFunction,
    clearStepperStateDeleteFormDraftFunction,
    setStepperStateSaveFormDraftFunction,
    clearStepperStateSaveFormDraftFunction
} from "redux/actions";
import AutoSaveFormValues from "components/AutoSaveFormValues";
import saveFormDraft from "libs/saveFormDraft";
import Confirm from "components/popup/confirm/Confirm";

const style = theme => ({
    root: {
        width: '100%',
        '&>*:last-child': {
            marginTop: theme.spacing()
        }
    },
    error: {
        color: theme.palette.error.main
    }
})

function PositionAndPartsForm(
    {
        classes,
        match,
        expanded,
        section,
        formId,
        dealingsList,
        dealing,
        setStepperStateCloseValidator,
        clearStepperStateCloseValidator,
        setStepperStateDeleteFormDraftFunction,
        clearStepperStateDeleteFormDraftFunction,
        setStepperStateSaveFormDraftFunction,
        clearStepperStateSaveFormDraftFunction,
        saveSuccessCallback,
        saveErrorCallback,
        demandHeader
    }) {
    const [afterSave, setAfterSave] = useState(false);
    const editable = section ? section.editable : true;

    const realDirty = useRef("nie");
    const dirtyListenerSemaphore = useRef(false);

    const id = match.params.id;
    const autoSaveFormDraftActive = demandHeader?.applicationClientSettings.autoSaveFormDraft.active && dealingsList?.dealings?.length <= 30;

    const [dealingValuesAndActionsForSave, setDealingValuesAndActionsForSave] = useState(null);
    const [getDraftsAndSaveDealingsConfirm, setGetDraftsAndSaveDealingsConfirm] = useState(false);

    useEffect(() => {
        if (expanded) {
            setStepperStateCloseValidator(() => {
                return realDirty.current !== "tak"
            });
            if (autoSaveFormDraftActive) {
                setStepperStateDeleteFormDraftFunction(() => DealingsService.deleteDraft(id));
                setStepperStateSaveFormDraftFunction(() => saveFormDraft(id, DealingsService.saveDraft, formId));
            }
        } else {
            dirtyListenerSemaphore.current = false;
            realDirty.current = "nie";
        }
        return () => {
            clearStepperStateCloseValidator();
            clearStepperStateDeleteFormDraftFunction();
            clearStepperStateSaveFormDraftFunction();
        }

    }, [id, formId, expanded, autoSaveFormDraftActive, setStepperStateCloseValidator, clearStepperStateCloseValidator,
        setStepperStateDeleteFormDraftFunction, clearStepperStateDeleteFormDraftFunction, setStepperStateSaveFormDraftFunction, clearStepperStateSaveFormDraftFunction])

    const handleSubmit = (values, actions) => {
        if (autoSaveFormDraftActive && dealingsList.dealings.length > (!!dealing ? Object.keys(dealing).length : 0)) {
            const draftPromises = [];
            dealingsList.dealings.filter(d => !!dealing ? !dealing[d.identity] : true).forEach(d => draftPromises.push(DealingsService.checkForDraft(id, d.identity)));
            Promise.all(draftPromises).then(promisesValues => {
                if (promisesValues.some(v => v.result === true)) {
                    setDealingValuesAndActionsForSave([values, actions]);
                    setGetDraftsAndSaveDealingsConfirm(true);
                } else {
                    doSaveDealings(values, actions);
                }
            });
        } else {
            doSaveDealings(values, actions);
        }
    }

    const getAndInitializeDraftsThenSaveDealings = () => {
        const values = dealingValuesAndActionsForSave[0];
        const actions = dealingValuesAndActionsForSave[1];
        const draftPromises = [];
        dealingsList.dealings.filter(d => !!dealing ? !dealing[d.identity] : true).forEach(d => draftPromises.push(DealingsService.loadDraft(id, d.identity)));
        Promise.all(draftPromises).then(promisesValues => {
            promisesValues.forEach(v => {
                if (v.success) {
                    const dealingDraft = v.result;
                    dealingDraft._touched = true;
                    const tmpArr = values.form.filter(i => i.identity !== dealingDraft.identity);
                    values.form = [
                        ...tmpArr,
                        dealingDraft
                    ]

                }
            });
            setDealingValuesAndActionsForSave(null);
            doSaveDealings(values, actions);
        });
    }

    const doNotInitializeDraftsThenSaveDealings = () => {
        const values = dealingValuesAndActionsForSave[0];
        const actions = dealingValuesAndActionsForSave[1];
        setDealingValuesAndActionsForSave(null);
        doSaveDealings(values, actions);
    }

    const doSaveDealings = (values, actions) => {
        DealingsService.save(id, values)
            .then((success) => {

                if(success) {
                    realDirty.current = "nie";
                    saveSuccessCallback();
                    actions.setValues({
                        form: values.form.map(element => ({
                            ...element,
                            _touched: false,
                            _open: false,
                            _edit: false,
                        }))
                    })
                } else {
                    saveErrorCallback();
                }

            })
    }

    if (!dealingsList && id) {
        return <Loading/>
    }

    const formatDealingList = (list = []) => {
        const _openTouched = list.length === 1;
        return list.map(item => ({
            _open: _openTouched,
            _touched: _openTouched,
            _edit: false,
            ...item,
            stages: [{
                conditions: [],
                criterions: [],
                parameters: [],
            }],
        }));
    }

    const getInitialValues = () => {
        if (id) {
            return formatDealingList(dealingsList.dealings)
        }
        return initialValues
    }

    const handleChange = (e) => {
        if (dirtyListenerSemaphore.current === false) {
            realDirty.current = "tak";
        }
    }

    return (
        <>
            <Form
                className={classes.root}
                onSubmit={handleSubmit}
                initialValues={{
                    form: getInitialValues(),
                    _editable: editable,
                }}
                id={formId}
                validationSchema={validationSchema(dealingsList)}
                afterSubmitFunc={() => {
                    setAfterSave(true);
                    if (isDemandModification()) {
                        ModificationService.isDemandChanged(match.params.id);
                    }
                }}
            >
                {(_formikProps) => {
                    const formikProps = {
                        ..._formikProps,
                        setValues: (values, validate) => {
                            handleChange();
                            return _formikProps.setValues(values, validate)
                        },
                        setFieldValue: (field, value, validate) => {
                            handleChange();
                            return _formikProps.setFieldValue(field, value, validate)
                        },
                        handleChange: (e) => {
                            handleChange();
                            return _formikProps.handleChange(e);
                        }
                    }
                    return (
                        <>

                            <DemandValidationMessages editable={editable} section={section}/>
                            <ExecuteFunction
                                func={() => {
                                    setFormikValuesTouched(formikProps)
                                    setAfterSave(false)
                                }}
                                shouldExecute={afterSave === true}
                            />
                            <SaveFormikValues
                                formikProps={formikProps}
                                id={formId}
                            />
                            {autoSaveFormDraftActive &&
                            <AutoSaveFormValues
                                formikProps={formikProps}
                                id={id}
                                formId={formId}
                                saveFunc={(formValues) => {
                                    DealingsService.saveDraft(id, formValues)
                                }}
                                customDirtyRef={realDirty}
                            />
                            }
                            <SortableContainer
                                formikProps={formikProps}
                                id="form"
                            >
                                {(formikProps, id, index) => {
                                    return (
                                        <div>
                                            <Element
                                                id={id}
                                                formikProps={formikProps}
                                                index={index}
                                                dirtyListenerSemaphore={dirtyListenerSemaphore}
                                                autoSaveFormDraftActive={autoSaveFormDraftActive}
                                            />
                                            <Divider className={classes.divider}/>
                                        </div>
                                    )
                                }}
                            </SortableContainer>

                            <AdditionalButtons
                                id="form"
                                formikProps={formikProps}
                            />

                        </>
                    )
                }}
            </Form>
            <Confirm confirmText='dealings_save_found_unloaded_drafts'
                     onCancel={() => {
                         setGetDraftsAndSaveDealingsConfirm(false);
                         doNotInitializeDraftsThenSaveDealings();
                     }}
                     onConfirm={() => {
                         setGetDraftsAndSaveDealingsConfirm(false);
                         getAndInitializeDraftsThenSaveDealings();
                     }}
                     open={getDraftsAndSaveDealingsConfirm}
            />
        </>
    )
}

PositionAndPartsForm.propTypes = {
    expanded: PropTypes.bool.isRequired,
    section: PropTypes.shape({
        editable: PropTypes.bool,
        messages: PropTypes.arrayOf(PropTypes.string),
        infoMessages: PropTypes.arrayOf(PropTypes.string),
    }),
    formId: PropTypes.string.isRequired,
    saveSuccessCallback: PropTypes.func.isRequired,
    saveErrorCallback: PropTypes.func.isRequired
}

export default connect((state) => ({
    dealingsList: state.resources.dealingsList,
    dealing: state.resources.dealing,
    demandHeader: state.resources.demandHeader
}), {setStepperStateCloseValidator, clearStepperStateCloseValidator,
    setStepperStateDeleteFormDraftFunction, clearStepperStateDeleteFormDraftFunction,
    setStepperStateSaveFormDraftFunction, clearStepperStateSaveFormDraftFunction
})(withStyles(style)(withRouter(PositionAndPartsForm)));